import { useState, type ReactNode } from "react";
import {
  ArrowLeft,
  ArrowRight,
  CalendarDays,
  Check,
  ChevronDown,
  ChevronUp,
  Clock,
  HeartPulse,
  Home,
  Mail,
  MapPin,
  MessageSquareText,
  Moon,
  MoreHorizontal,
  PawPrint,
  Phone,
  StickyNote,
  Sun,
  User,
  Utensils,
} from "lucide-react";

import { formatDate } from "../../i18n/format";
import { geslachtWoord, soortGroep, type Regel, type Status, type Veld, type Weergave as Soort } from "./velden";
import { IndeelbareTabel } from "../../components/IndeelbareTabel";

type Vertaal = (key: string, options?: { args?: unknown[] }) => string;

export interface Ctx {
  t: Vertaal;
  locale: string;
  vandaag: string;
  datum: string;
  fotos: Record<string, string>;
  velden: Set<Veld>;
  /** Per reservering gegroepeerd: wat voor de hele reservering geldt staat dan in de kop, niet in elke tegel. */
  gegroepeerd?: boolean;
  /** Het dier openen; met klantId opent de pension het klantscherm met dit dier gekozen. */
  onOpenDier?: (dierId: number, klantId?: number | null) => void;
  /** De klant openen; met dierId staat dat dier (van de regel of reservering) meteen open. */
  onOpenKlant?: (klantId: number, dierId?: number) => void;
}

const LEEG = "—";

export function reserveringCode(r: Pick<Regel, "soort" | "reserveringNummer" | "reserveringId">): string {
  const nr = r.reserveringNummer ?? r.reserveringId;
  if (nr == null) return LEEG;
  return `${r.soort === "DAGOPVANG" ? "D" : "R"}-${nr}`;
}

export function verblijfCode(r: Pick<Regel, "soort" | "regelId">): string | null {
  return r.regelId == null ? null : `${r.soort === "DAGOPVANG" ? "D" : "P"}-${r.regelId}`;
}

function tijd(moment: string | null): string {
  return moment ? moment.slice(11, 16) : "";
}

/** "Komt vandaag" alleen als de gekozen dag vandaag is; op een andere dag is het gewoon "Komt". */
function statusTekst(status: Status, ctx: Ctx): string {
  const vandaag = ctx.datum === ctx.vandaag;
  if (status === "KOMT" || status === "VERTREKT") {
    return ctx.t(`pensionOverzichten.status.${status}${vandaag ? ".vandaag" : ""}`);
  }
  return ctx.t(`pensionOverzichten.status.${status}`);
}

export function StatusLabel({ r, ctx, klein }: { r: Regel; ctx: Ctx; klein?: boolean }) {
  const Icoon = r.status === "KOMT" ? ArrowRight : r.status === "VERTREKT" ? ArrowLeft : r.status === "GEPLAND" ? CalendarDays : Check;
  return (
    <span className="po-status" data-status={r.status} data-klein={klein || undefined}>
      <Icoon size={13} aria-hidden="true" />
      {statusTekst(r.status, ctx)}
    </span>
  );
}

function Foto({ r, ctx, className }: { r: Regel; ctx: Ctx; className: string }) {
  const url = ctx.fotos[String(r.dierId)];
  return url ? (
    <img className={className} src={url} alt="" />
  ) : (
    <span className={`${className} po-foto--leeg`} aria-hidden="true">
      <PawPrint size={28} />
    </span>
  );
}

function Naam({ r, ctx }: { r: Regel; ctx: Ctx }) {
  const naam = r.dierNaam ?? LEEG;
  return ctx.onOpenDier ? (
    <button type="button" className="po-naam" onClick={() => ctx.onOpenDier!(r.dierId, r.klantId)}>
      {naam}
    </button>
  ) : (
    <strong className="po-naam">{naam}</strong>
  );
}

function Klant({ r, ctx }: { r: Regel; ctx: Ctx }) {
  const naam = r.klantNaam ?? LEEG;
  return ctx.onOpenKlant && r.klantId != null ? (
    <button type="button" className="po-link" onClick={() => ctx.onOpenKlant!(r.klantId!, r.dierId)}>
      {naam}
    </button>
  ) : (
    <>{naam}</>
  );
}

/** Ras, soort en geslacht op één regel, zoals onder de naam op de tegel. */
function Onderregel({ r, ctx, metSoort }: { r: Regel; ctx: Ctx; metSoort?: boolean }) {
  const delen = [
    ctx.velden.has("ras") ? r.ras : null,
    metSoort ? r.diersoort : null,
    ctx.velden.has("ras") ? geslachtWoord(r, ctx.t) : null,
  ].filter(Boolean);
  return delen.length ? <span className="po-onder">{delen.join(" • ")}</span> : null;
}

function Verblijf({ r, ctx }: { r: Regel; ctx: Ctx }) {
  if (r.soort === "DAGOPVANG") {
    return (
      <>
        {formatDate(r.van, ctx.locale)} {tijd(r.brengen)}–{tijd(r.halen)}
      </>
    );
  }
  return (
    <>
      {formatDate(r.van, ctx.locale)} <ArrowRight size={12} aria-hidden="true" /> {formatDate(r.tot, ctx.locale)}
    </>
  );
}

/** De regels met een icoon ervoor, in de volgorde van de velden. */
function Gegevens({ r, ctx }: { r: Regel; ctx: Ctx }) {
  const v = ctx.velden;
  const rijen: Array<[string, ReactNode, ReactNode]> = [];
  if (v.has("eigenaar")) rijen.push(["eigenaar", <User size={14} />, <Klant r={r} ctx={ctx} />]);
  if (v.has("telefoon") && r.telefoon)
    rijen.push(["telefoon", <Phone size={14} />, <a className="po-link" href={`tel:${r.telefoon}`}>{r.telefoon}</a>]);
  if (v.has("email") && r.email)
    rijen.push(["email", <Mail size={14} />, <a className="po-link" href={`mailto:${r.email}`}>{r.email}</a>]);
  if (v.has("woonplaats") && r.woonplaats) rijen.push(["woonplaats", <MapPin size={14} />, r.woonplaats]);
  if (v.has("reservering")) rijen.push(["reservering", <CalendarDays size={14} />, reserveringCode(r)]);
  if (v.has("kennel") && r.soort === "PENSION")
    rijen.push(["kennel", <Home size={14} />, r.kennel ?? <span className="po-zacht">{ctx.t("pensionOverzichten.nietIngedeeld")}</span>]);
  if (v.has("verblijf")) rijen.push(["verblijf", r.soort === "DAGOPVANG" ? <Clock size={14} /> : <CalendarDays size={14} />, <Verblijf r={r} ctx={ctx} />]);
  if (v.has("nachten") && r.soort === "PENSION")
    rijen.push(["nachten", <Moon size={14} />, ctx.t("pensionOverzichten.nachten", { args: [r.nachten] })]);
  if (rijen.length === 0) return null;
  return (
    <dl className="po-gegevens">
      {rijen.map(([sleutel, icoon, waarde]) => (
        <div key={sleutel} className="po-gegevens__rij">
          <dt>
            <span aria-hidden="true">{icoon}</span>
            {ctx.t(`pensionOverzichten.veld.${sleutel}`)}
          </dt>
          <dd>{waarde}</dd>
        </div>
      ))}
    </dl>
  );
}

/**
 * Voeding, medicatie en bijzonderheden als chips; de details staan in de tooltip. De lijst heeft voor de
 * bijzonderheden een eigen kolom met de tekst, en laat die chip daarom weg.
 */
function Verzorging({
  r,
  ctx,
  metAankomst = true,
  metBijzonderheden = true,
}: {
  r: Regel;
  ctx: Ctx;
  metAankomst?: boolean;
  metBijzonderheden?: boolean;
}) {
  const v = ctx.velden;
  const chips: ReactNode[] = [];
  if (metAankomst && r.status === "KOMT" && ctx.datum === ctx.vandaag && r.soort === "PENSION") {
    chips.push(
      <span key="komt" className="po-chip" data-toon="accent">
        <CalendarDays size={13} aria-hidden="true" />
        {ctx.t("pensionOverzichten.chip.aankomstVandaag")}
      </span>,
    );
  }
  if (v.has("voeding") && r.voeding) {
    const eigen = r.voeding === "EIGEN";
    const standaard = r.voeding === "STANDAARD";
    chips.push(
      <span key="voeding" className="po-chip" title={standaard || eigen ? undefined : r.voeding}>
        <Utensils size={13} aria-hidden="true" />
        {eigen ? ctx.t("pensionOverzichten.chip.eigenVoeding") : ctx.t("pensionOverzichten.chip.voeding")}
      </span>,
    );
  }
  if (v.has("medicatie") && r.medicatie) {
    chips.push(
      <span key="medicatie" className="po-chip" data-toon="rood" title={r.medicatieInstructies ?? undefined}>
        <HeartPulse size={13} aria-hidden="true" />
        {ctx.t("pensionOverzichten.chip.medicatie")}
      </span>,
    );
  }
  if (metBijzonderheden && v.has("bijzonderheden") && r.bijzonderheden) {
    chips.push(
      <span key="bijz" className="po-chip" data-toon="amber" title={r.bijzonderheden}>
        <StickyNote size={13} aria-hidden="true" />
        {ctx.t("pensionOverzichten.chip.bijzonderheden")}
      </span>,
    );
  }
  if (r.soort === "DAGOPVANG") {
    chips.push(
      <span key="dag" className="po-chip" data-toon="geel">
        <Sun size={13} aria-hidden="true" />
        {ctx.t("pensionOverzichten.chip.dagopvang")}
      </span>,
    );
  }
  return chips.length ? <div className="po-chips">{chips}</div> : null;
}

/** Wat een verzorger bij het dier moet weten, voluit. */
function Toelichting({ r, ctx }: { r: Regel; ctx: Ctx }) {
  if (!ctx.velden.has("bijzonderheden")) return null;
  const regels = [
    r.medicatie && r.medicatieInstructies ? `${ctx.t("pensionOverzichten.chip.medicatie")}: ${r.medicatieInstructies}` : null,
    r.bijzonderheden,
  ].filter(Boolean);
  return regels.length ? <p className="po-toelichting">{regels.join(" · ")}</p> : null;
}

/** De pensionopmerking op de tegel; gegroepeerd staat die eenmaal in de kop van de reservering. */
function Opmerking({ r, ctx }: { r: Regel; ctx: Ctx }) {
  if (ctx.gegroepeerd || !ctx.velden.has("opmerking") || !r.opmerking) return null;
  return <OpmerkingTekst tekst={r.opmerking} ctx={ctx} />;
}

function OpmerkingTekst({ tekst, ctx, className = "" }: { tekst: string; ctx: Ctx; className?: string }) {
  return (
    <p className={`po-opmerking ${className}`.trim()}>
      <MessageSquareText size={13} aria-hidden="true" />
      <span>
        <span className="po-verborgen">{ctx.t("pensionOverzichten.veld.opmerking")}: </span>
        {tekst}
      </span>
    </p>
  );
}

function Badge({ r, ctx }: { r: Regel; ctx: Ctx }) {
  const code = ctx.velden.has("verblijfnr") ? verblijfCode(r) : null;
  return code ? <span className="po-badge">{code}</span> : null;
}

/** De kleine tegel: foto links, gegevens rechts. */
export function KleineTegel({ r, ctx }: { r: Regel; ctx: Ctx }) {
  return (
    <article className="po-tegel po-tegel--klein" aria-label={r.dierNaam ?? undefined}>
      <div className="po-tegel__fotovak">
        <Foto r={r} ctx={ctx} className="po-tegel__foto" />
        <Badge r={r} ctx={ctx} />
      </div>
      <div className="po-tegel__inhoud">
        {ctx.velden.has("status") && (
          <div className="po-tegel__statusrij">
            <StatusLabel r={r} ctx={ctx} klein />
          </div>
        )}
        <Naam r={r} ctx={ctx} />
        <Onderregel r={r} ctx={ctx} />
        <Gegevens r={r} ctx={ctx} />
        <Toelichting r={r} ctx={ctx} />
        <Opmerking r={r} ctx={ctx} />
        <Verzorging r={r} ctx={ctx} />
      </div>
    </article>
  );
}

/** De grote tegel: de foto als banner bovenaan, met nummer en status erop. */
export function GroteTegel({ r, ctx }: { r: Regel; ctx: Ctx }) {
  return (
    <article className="po-tegel po-tegel--groot" aria-label={r.dierNaam ?? undefined}>
      <div className="po-tegel__banner">
        <Foto r={r} ctx={ctx} className="po-tegel__bannerfoto" />
        <Badge r={r} ctx={ctx} />
        {ctx.velden.has("status") && (
          <span className="po-tegel__bannerstatus">
            <StatusLabel r={r} ctx={ctx} klein />
          </span>
        )}
      </div>
      <div className="po-tegel__inhoud">
        <Naam r={r} ctx={ctx} />
        <Onderregel r={r} ctx={ctx} metSoort />
        <Gegevens r={r} ctx={ctx} />
        <Toelichting r={r} ctx={ctx} />
        <Opmerking r={r} ctx={ctx} />
        <Verzorging r={r} ctx={ctx} />
      </div>
    </article>
  );
}

export function Tegels({ regels, ctx, soort }: { regels: Regel[]; ctx: Ctx; soort: Exclude<Soort, "LIJST"> }) {
  const Tegel = soort === "GROOT" ? GroteTegel : KleineTegel;
  return (
    <div className="po-raster" data-soort={soort}>
      {regels.map((r) => (
        <Tegel key={`${r.soort}-${r.regelId ?? r.dierId}`} r={r} ctx={ctx} />
      ))}
    </div>
  );
}

function Acties({ r, ctx }: { r: Regel; ctx: Ctx }) {
  const [open, setOpen] = useState(false);
  if (!ctx.onOpenDier && !ctx.onOpenKlant) return null;
  return (
    <span className="po-acties">
      <button
        type="button"
        className="po-icoonknop"
        aria-label={ctx.t("pensionOverzichten.acties", { args: [r.dierNaam ?? ""] })}
        aria-expanded={open}
        onClick={() => setOpen((x) => !x)}
      >
        <MoreHorizontal size={16} aria-hidden="true" />
      </button>
      {open && (
        <span className="po-menu" role="menu" onMouseLeave={() => setOpen(false)}>
          {ctx.onOpenDier && (
            <button type="button" role="menuitem" onClick={() => ctx.onOpenDier!(r.dierId, r.klantId)}>
              {ctx.t("pensionOverzichten.openDier")}
            </button>
          )}
          {ctx.onOpenKlant && r.klantId != null && (
            <button type="button" role="menuitem" onClick={() => ctx.onOpenKlant!(r.klantId!, r.dierId)}>
              {ctx.t("pensionOverzichten.openKlant")}
            </button>
          )}
        </span>
      )}
    </span>
  );
}

/** De lijst: één regel per dier, met de kolommen van de gekozen velden. */
export function Lijst({ regels, ctx }: { regels: Regel[]; ctx: Ctx }) {
  const v = ctx.velden;
  const t = ctx.t;
  const verzorging = v.has("voeding") || v.has("medicatie");
  const opmerking = v.has("opmerking") && !ctx.gegroepeerd;
  return (
    <div className="po-lijstwrap">
      <IndeelbareTabel
        tabelKey="pension-aanwezig-lijst"
        columns={[
          { key: "foto", header: t("pensionOverzichten.kolom.foto"), vast: true,
            cell: (r) => <Foto r={r} ctx={ctx} className="po-lijst__foto" /> },
          ...(v.has("verblijfnr") ? [{
            key: "verblijfnr", header: t("pensionOverzichten.veld.verblijfnr"),
            cell: (r: Regel) => <span className="po-zacht">{verblijfCode(r) ?? LEEG}</span>,
          }] : []),
          {
            key: "dier", header: t("pensionOverzichten.kolom.dier"),
            cell: (r) => (
              <div className="po-lijst__dier">
                <Naam r={r} ctx={ctx} />
                <Onderregel r={r} ctx={ctx} />
              </div>
            ),
          },
          ...(v.has("eigenaar") ? [{
            key: "eigenaar", header: t("pensionOverzichten.veld.eigenaar"),
            cell: (r: Regel) => <Klant r={r} ctx={ctx} />,
          }] : []),
          ...(v.has("telefoon") ? [{
            key: "telefoon", header: t("pensionOverzichten.veld.telefoon"),
            cell: (r: Regel) => r.telefoon ?? LEEG,
          }] : []),
          ...(v.has("email") ? [{
            key: "email", header: t("pensionOverzichten.veld.email"),
            cell: (r: Regel) => r.email ?? LEEG,
          }] : []),
          ...(v.has("woonplaats") ? [{
            key: "woonplaats", header: t("pensionOverzichten.veld.woonplaats"),
            cell: (r: Regel) => r.woonplaats ?? LEEG,
          }] : []),
          ...(v.has("reservering") ? [{
            key: "reservering", header: t("pensionOverzichten.veld.reservering"),
            cell: (r: Regel) => reserveringCode(r),
          }] : []),
          ...(v.has("kennel") ? [{
            key: "kennel", header: t("pensionOverzichten.veld.kennel"),
            cell: (r: Regel) => (r.soort === "PENSION" ? (r.kennel ?? LEEG) : LEEG),
          }] : []),
          ...(v.has("verblijf") ? [{
            key: "van", header: t("pensionOverzichten.kolom.van"),
            cell: (r: Regel) => (
              <>
                {formatDate(r.van, ctx.locale)}
                {r.soort === "DAGOPVANG" && ` ${tijd(r.brengen)}`}
              </>
            ),
          }, {
            key: "tot", header: t("pensionOverzichten.kolom.tot"),
            cell: (r: Regel) => (
              <>
                {formatDate(r.tot, ctx.locale)}
                {r.soort === "DAGOPVANG" && ` ${tijd(r.halen)}`}
              </>
            ),
          }] : []),
          ...(v.has("nachten") ? [{
            key: "nachten", header: t("pensionOverzichten.veld.nachten"), align: "right" as const,
            cell: (r: Regel) => <span className="po-getal">{r.soort === "PENSION" ? r.nachten : LEEG}</span>,
          }] : []),
          ...(v.has("status") ? [{
            key: "status", header: t("pensionOverzichten.veld.status"),
            cell: (r: Regel) => <StatusLabel r={r} ctx={ctx} />,
          }] : []),
          ...(verzorging ? [{
            key: "verzorging", header: t("pensionOverzichten.kolom.verzorging"),
            cell: (r: Regel) => (
              <Verzorging r={r} ctx={ctx} metAankomst={!v.has("status")} metBijzonderheden={false} />
            ),
          }] : []),
          ...(v.has("bijzonderheden") ? [{
            key: "bijzonderheden", header: t("pensionOverzichten.veld.bijzonderheden"),
            // Lange vrije tekst: afgekapt in de cel, voluit in de tooltip.
            cell: (r: Regel) => (
              <span className="po-lijst__tekst" title={r.bijzonderheden ?? undefined}>
                {r.bijzonderheden ?? LEEG}
              </span>
            ),
          }] : []),
          ...(opmerking ? [{
            key: "opmerking", header: t("pensionOverzichten.veld.opmerking"),
            cell: (r: Regel) => (
              <span className="po-lijst__tekst" title={r.opmerking ?? undefined}>
                {r.opmerking ?? LEEG}
              </span>
            ),
          }] : []),
          {
            key: "acties", header: "", vast: true,
            cell: (r) => <span className="po-lijst__acties"><Acties r={r} ctx={ctx} /></span>,
          },
        ]}
        rows={regels}
        getRowKey={(r) => `${r.soort}-${r.regelId ?? r.dierId}`}
        empty=""
      />
    </div>
  );
}

/** De kop van een reservering: de klant, het nummer, de periode en wat er meekomt. */
export function GroepKop({
  regels,
  ctx,
  open,
  onOpen,
}: {
  regels: Regel[];
  ctx: Ctx;
  open: boolean;
  onOpen: () => void;
}) {
  const r = regels[0];
  const t = ctx.t;
  const van = regels.reduce((m, x) => (x.van < m ? x.van : m), r.van);
  const tot = regels.reduce((m, x) => (x.tot > m ? x.tot : m), r.tot);
  const perSoort = new Map<string, number>();
  for (const x of regels) {
    const s = soortGroep(x.diersoort);
    perSoort.set(s, (perSoort.get(s) ?? 0) + 1);
  }
  return (
    <div className="po-groep__kop">
      <span className="po-groep__persoon" aria-hidden="true">
        <User size={20} />
      </span>
      <div className="po-groep__klant">
        <h3>
          {ctx.onOpenKlant && r.klantId != null ? (
            <button type="button" className="po-link po-link--kop" onClick={() => ctx.onOpenKlant!(r.klantId!, r.dierId)}>
              {r.klantNaam ?? LEEG}
            </button>
          ) : (
            (r.klantNaam ?? LEEG)
          )}
        </h3>
      </div>
      <div className="po-groep__contact">
        {r.email && (
          <span>
            <Mail size={14} aria-hidden="true" />
            {r.email}
          </span>
        )}
        {r.telefoon && (
          <span>
            <Phone size={14} aria-hidden="true" />
            {r.telefoon}
          </span>
        )}
      </div>
      <div className="po-groep__res">
        <span className="po-zacht">{t(r.soort === "DAGOPVANG" ? "pensionOverzichten.groep.dagopvang" : "pensionOverzichten.groep.reservering")}</span>
        <strong>{reserveringCode(r)}</strong>
      </div>
      <div className="po-groep__periode">
        {r.soort === "DAGOPVANG" ? <Sun size={16} aria-hidden="true" /> : <CalendarDays size={16} aria-hidden="true" />}
        <span className="po-zacht">{t("pensionOverzichten.veld.verblijf")}</span>
        <span>
          {formatDate(van, ctx.locale)}
          {r.soort === "PENSION" && (
            <>
              {" "}
              <ArrowRight size={13} aria-hidden="true" /> {formatDate(tot, ctx.locale)}
            </>
          )}
        </span>
      </div>
      {r.soort === "PENSION" && (
        <div className="po-groep__nachten">
          <Moon size={16} aria-hidden="true" />
          {t("pensionOverzichten.nachten", { args: [r.nachten] })}
        </div>
      )}
      {ctx.velden.has("opmerking") && r.opmerking && <OpmerkingTekst tekst={r.opmerking} ctx={ctx} className="po-groep__opmerking" />}
      <div className="po-groep__tellers">
        <span className="po-pil" data-sterk="">
          {t(regels.length === 1 ? "pensionOverzichten.aantalDieren.een" : "pensionOverzichten.aantalDieren", { args: [regels.length] })}
        </span>
        {(["HOND", "KAT", "OVERIG"] as const)
          .filter((s) => perSoort.get(s))
          .map((s) => (
            <span key={s} className="po-pil">
              {t(`pensionOverzichten.aantal.${s}.${perSoort.get(s) === 1 ? "een" : "meer"}`, { args: [perSoort.get(s)] })}
            </span>
          ))}
      </div>
      <button
        type="button"
        className="po-icoonknop"
        aria-expanded={open}
        aria-label={t(open ? "pensionOverzichten.groep.inklappen" : "pensionOverzichten.groep.uitklappen", { args: [r.klantNaam ?? ""] })}
        onClick={onOpen}
      >
        {open ? <ChevronUp size={18} aria-hidden="true" /> : <ChevronDown size={18} aria-hidden="true" />}
      </button>
    </div>
  );
}
